import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { routesApi, storesApi, projectsApi, type Route, type MyRoutesResponse, type Project, type CallLog, isVisitSigned, EDIT_REASONS } from '../services/api';
import { useDayTrackingContext } from '../context/DayTrackingContext';
import type { EditVisitInput } from '../sync/ops/editVisit';
import {
  getMinSelectableDate,
  getMaxSelectableDate,
  isDateEditable,
  formatDateToLocal,
  getEditablePeriodDescription,
} from '../utils/timesheetRules';
import TimeConflictEditor, { parseConflictFromError, type ConflictingEntry } from './TimeConflictEditor';
import { buildCallLogEditPatch } from '../utils/callLogEdit';

// Helper to parse time from ISO or HH:MM format
function parseTime(timeStr: string | null): string {
  if (!timeStr) return '';
  // If it's a full ISO datetime, extract just HH:MM
  if (timeStr.includes('T')) {
    const date = new Date(timeStr);
    return date.toTimeString().slice(0, 5);
  }
  // If it's already HH:MM or HH:MM:SS, return first 5 chars
  return timeStr.slice(0, 5);
}

// Store type for combobox selection
interface ProjectStore {
  id: number;
  legacystoreid: number;
  name: string;
  address: string;
  retailer_abbreviation: string;
  storenumber: string;
}

interface ManualCallEntryProps {
  isOpen: boolean;
  onClose: () => void;
  editingCallLog?: CallLog | null; // When provided, opens in edit mode
  onSaveComplete?: () => void; // Called after successful edit save
  /** Seed times for the span the caller already knows (a gap the rep tapped),
   * so re-asking cannot invite a mistyped range that leaves the hole standing
   * or overlaps what exists. In ADD mode it seeds the visit's date and times;
   * in EDIT mode it seeds the DRIVE fields (attaching the gap as the visit's
   * drive) — and only when the visit has no drive times of its own, because
   * existing values are the rep's record and are never silently replaced. */
  initialSpan?: { date: string; timeIn: string; timeOut: string };
}

type Step = 'select' | 'unassigned' | 'date' | 'details' | 'confirm';

// Reason options for unassigned calls
const UNASSIGNED_REASONS = [
  { value: 'coverage', label: 'Coverage for absent colleague' },
  { value: 'training', label: 'Training / Ride-along' },
  { value: 'emergency', label: 'Emergency coverage' },
  { value: 'requested', label: 'Store/Manager requested visit' },
  { value: 'route_change', label: 'Route change pending update' },
  { value: 'other', label: 'Other' },
] as const;

interface SelectedRoute {
  routeId: number | null; // null for unassigned calls
  storeId: number;
  storeName: string;
  storeAddress: string;
  projectId: number;
  projectName: string;
  distributorId: number | null;
  retailerAbbrev: string;
  storeNumber: string;
  isUnassigned?: boolean;
  unassignedReason?: string;
  unassignedReasonOther?: string;
}


export default function ManualCallEntry({
  isOpen,
  onClose,
  editingCallLog,
  onSaveComplete,
  initialSpan,
}: ManualCallEntryProps) {
  const navigate = useNavigate();
  const isEditMode = !!editingCallLog;
  const [step, setStep] = useState<Step>('select');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [routesData, setRoutesData] = useState<MyRoutesResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoute, setSelectedRoute] = useState<SelectedRoute | null>(null);
  const [selectedDate, setSelectedDate] = useState('');

  // Edit mode state
  const isSigned = isEditMode && editingCallLog ? isVisitSigned(editingCallLog) : false;
  const { applyVisitTimeCorrection } = useDayTrackingContext();

  // Unassigned call state - combobox selection
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [projectStores, setProjectStores] = useState<ProjectStore[]>([]);
  // Projects filtered to a chosen store - inverse cross-filter. #1851
  const [projectsForStore, setProjectsForStore] = useState<Project[]>([]);
  // Server-side typeahead results when no project is selected. #1851
  const [storeSearchResults, setStoreSearchResults] = useState<ProjectStore[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [isLoadingProjectsForStore, setIsLoadingProjectsForStore] = useState(false);
  const [isSearchingStores, setIsSearchingStores] = useState(false);

  // Combobox state
  const [projectSearch, setProjectSearch] = useState('');
  const [storeSearch, setStoreSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedStore, setSelectedStore] = useState<ProjectStore | null>(null);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const projectDropdownRef = useRef<HTMLDivElement>(null);
  const storeDropdownRef = useRef<HTMLDivElement>(null);
  // Debounce timer for server-side store search.
  const storeSearchDebounceRef = useRef<number | null>(null);

  const [unassignedReason, setUnassignedReason] = useState('');
  const [unassignedReasonOther, setUnassignedReasonOther] = useState('');

  // #3525 — a signed visit's times change only with a reason on the record.
  const [editReason, setEditReason] = useState('');
  // Visit details state
  const [driveTimeStart, setDriveTimeStart] = useState('');
  const [driveTimeEnd, setDriveTimeEnd] = useState('');
  const [driveStartOdo, setDriveStartOdo] = useState('');
  const [driveEndOdo, setDriveEndOdo] = useState('');
  const [timeIn, setTimeIn] = useState('');
  const [timeOut, setTimeOut] = useState('');
  const [isTeamProject, setIsTeamProject] = useState(false);
  const [isTeamLeader, setIsTeamLeader] = useState(false);
  const [visitNotes, setVisitNotes] = useState('');

  // Time conflict state (for inline editing when overlap errors occur)
  const [timeConflicts, setTimeConflicts] = useState<ConflictingEntry[]>([]);
  const [showTimeConflictEditor, setShowTimeConflictEditor] = useState(false);

  // Get editable date range
  const minDate = getMinSelectableDate();
  const maxDate = getMaxSelectableDate();
  const editablePeriod = getEditablePeriodDescription();

  // Fetch routes on open (only in add mode)
  useEffect(() => {
    if (!isOpen || editingCallLog) return; // Skip in edit mode

    async function fetchRoutes() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await routesApi.getMyRoutes();
        setRoutesData(data);
      } catch (err) {
        console.error('Failed to fetch routes:', err);
        setError(err instanceof Error ? err.message : 'Failed to load routes');
      } finally {
        setIsLoading(false);
      }
    }

    fetchRoutes();
  }, [isOpen, editingCallLog]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      // Reset time conflict state
      setTimeConflicts([]);
      setShowTimeConflictEditor(false);

      if (editingCallLog) {
        // EDIT MODE: Pre-populate from calllog
        const storeNum = editingCallLog.store_legacystoreid;
        // Build project name - use description if available, otherwise show project ID
        const projectName = editingCallLog.project_description
          || (editingCallLog.project_id ? `Project #${editingCallLog.project_id}` : 'Unknown Project');
        setSelectedRoute({
          routeId: null,
          storeId: storeNum,
          storeName: `${editingCallLog.retailer_abbreviation} #${storeNum}`,
          storeAddress: editingCallLog.store_city || '',
          projectId: editingCallLog.project_id || 0,
          projectName,
          distributorId: null,
          retailerAbbrev: editingCallLog.retailer_abbreviation,
          storeNumber: String(storeNum),
        });
        // Normalize date - handle both ISO and MM/DD/YYYY formats
        const dtecall = editingCallLog.dtecall;
        let normalizedDate = dtecall;
        if (dtecall.includes('T')) {
          normalizedDate = dtecall.split('T')[0];
        } else if (dtecall.includes('/')) {
          const parts = dtecall.split('/');
          if (parts.length === 3) {
            normalizedDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          }
        }
        setSelectedDate(normalizedDate);
        // Pre-populate visit details. A gap being attached as this visit's
        // drive seeds the drive fields — but never over times the visit
        // already has.
        const existingDriveStart = parseTime(editingCallLog.stdrivetime);
        const existingDriveEnd = parseTime(editingCallLog.eddrivetime);
        if (initialSpan && !existingDriveStart && !existingDriveEnd) {
          setDriveTimeStart(initialSpan.timeIn);
          setDriveTimeEnd(initialSpan.timeOut);
        } else {
          setDriveTimeStart(existingDriveStart);
          setDriveTimeEnd(existingDriveEnd);
        }
        setDriveStartOdo(editingCallLog.driveodostart?.toString() || '');
        setDriveEndOdo(editingCallLog.driveodoend?.toString() || '');
        setTimeIn(parseTime(editingCallLog.stcalltime));
        setTimeOut(parseTime(editingCallLog.edcalltime));
        setIsTeamProject(editingCallLog.teamproject || false);
        setIsTeamLeader(editingCallLog.teamleader || false);
        setVisitNotes(editingCallLog.notes || '');
        // A signed visit used to jump straight to a read-only confirm view,
        // so the Edit button that opened this did nothing at all — the dead
        // control Michael reported. Its times are SEALED, not frozen (#3525):
        // editable, with a reason recorded, which is the only shape the API
        // now accepts.
        setStep('details');
        setIsLoading(false);
      } else {
        // ADD MODE: Reset everything
        setStep('select');
        setSearchQuery('');
        setSelectedRoute(null);
        setSelectedDate(initialSpan?.date ?? formatDateToLocal(new Date()));
        // Reset unassigned state
        setProjectSearch('');
        setStoreSearch('');
        setSelectedProject(null);
        setSelectedStore(null);
        setProjectStores([]);
        setUnassignedReason('');
        setUnassignedReasonOther('');
        // Reset visit details
        setDriveTimeStart('');
        setDriveTimeEnd('');
        setDriveStartOdo('');
        setDriveEndOdo('');
        setTimeIn(initialSpan?.timeIn ?? '');
        setTimeOut(initialSpan?.timeOut ?? '');
        setIsTeamProject(false);
        setIsTeamLeader(false);
        setVisitNotes('');
      }
    }
  }, [isOpen, editingCallLog, initialSpan]);


  // Fetch all projects when entering unassigned flow
  useEffect(() => {
    if (step !== 'unassigned' || allProjects.length > 0) return;

    async function fetchProjects() {
      setIsLoadingProjects(true);
      try {
        // API returns wrapped response
        interface WrappedResponse {
          status: string;
          message: string;
          data: Project[];
        }
        const response = await projectsApi.getProjects() as unknown as WrappedResponse[];
        if (response && response.length > 0 && response[0].data) {
          setAllProjects(response[0].data);
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err);
        setError('Failed to load projects');
      } finally {
        setIsLoadingProjects(false);
      }
    }

    fetchProjects();
  }, [step, allProjects.length]);

  // Fetch stores when project is selected
  useEffect(() => {
    if (!selectedProject) {
      setProjectStores([]);
      setSelectedStore(null);
      setStoreSearch('');
      return;
    }

    const projectId = selectedProject.projectid;

    async function fetchStores() {
      setIsLoadingStores(true);
      try {
        const stores = await storesApi.getStoresByProject(projectId);
        setProjectStores(stores);
      } catch (err) {
        console.error('Failed to fetch stores:', err);
        setError('Failed to load stores for this project');
      } finally {
        setIsLoadingStores(false);
      }
    }

    fetchStores();
  }, [selectedProject]);

  // Server-side store typeahead when no project is selected. Debounced
  // 250ms so we don't hit the backend on every keystroke. Only fires
  // when there's no selectedProject - if a project IS selected, the
  // store picker already has its in-project list. #1851
  useEffect(() => {
    if (selectedProject) {
      setStoreSearchResults([]);
      return;
    }
    if (!storeSearch.trim() || selectedStore) {
      setStoreSearchResults([]);
      return;
    }
    if (storeSearchDebounceRef.current !== null) {
      window.clearTimeout(storeSearchDebounceRef.current);
    }
    storeSearchDebounceRef.current = window.setTimeout(async () => {
      setIsSearchingStores(true);
      try {
        const results = await storesApi.searchStores(storeSearch.trim());
        setStoreSearchResults(results);
      } catch (err) {
        console.error('Store search failed:', err);
        setStoreSearchResults([]);
      } finally {
        setIsSearchingStores(false);
      }
    }, 250);
    return () => {
      if (storeSearchDebounceRef.current !== null) {
        window.clearTimeout(storeSearchDebounceRef.current);
      }
    };
  }, [storeSearch, selectedProject, selectedStore]);

  // Click outside handlers for dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target as Node)) {
        setShowProjectDropdown(false);
      }
      if (storeDropdownRef.current && !storeDropdownRef.current.contains(event.target as Node)) {
        setShowStoreDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter routes by search query
  const filteredRoutes = useMemo(() => {
    if (!routesData?.routes) return [];

    const query = searchQuery.toLowerCase().trim();
    if (!query) return routesData.routes;

    return routesData.routes.filter((route) => {
      const storeName = route.store.store_name?.toLowerCase() || '';
      const retailerName = route.store.retailer_name?.toLowerCase() || '';
      const storeNumber = String(route.store.store_number || '').toLowerCase();
      const projectName = route.project_description?.toLowerCase() || '';
      const address = route.store.address?.toLowerCase() || '';
      const city = route.store.city?.toLowerCase() || '';

      return (
        storeName.includes(query) ||
        retailerName.includes(query) ||
        storeNumber.includes(query) ||
        projectName.includes(query) ||
        address.includes(query) ||
        city.includes(query)
      );
    });
  }, [routesData?.routes, searchQuery]);

  // Group routes by project
  const groupedRoutes = useMemo(() => {
    const groups = new Map<string, Route[]>();

    for (const route of filteredRoutes) {
      const projectKey = route.project_description || 'Other';
      if (!groups.has(projectKey)) {
        groups.set(projectKey, []);
      }
      groups.get(projectKey)!.push(route);
    }

    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredRoutes]);

  // Source for the project dropdown: when a store is selected, narrow to
  // that store's projects. Otherwise show the full project list. #1851
  const projectSourceList = useMemo(() => {
    return selectedStore ? projectsForStore : allProjects;
  }, [selectedStore, projectsForStore, allProjects]);

  // Filter projects by search
  const filteredProjects = useMemo(() => {
    if (!projectSearch.trim()) return projectSourceList;
    const query = projectSearch.toLowerCase().trim();
    return projectSourceList.filter((p) => {
      const idStr = String(p.projectid);
      const desc = (p.projectdesc || p.projectname || '').toLowerCase();
      return idStr.includes(query) || desc.includes(query);
    });
  }, [projectSourceList, projectSearch]);

  // Source for the store dropdown:
  //   - project selected -> stores within that project (client-side filtered below)
  //   - no project       -> server-side typeahead results
  // #1851
  const storeSourceList = useMemo<ProjectStore[]>(() => {
    return selectedProject ? projectStores : storeSearchResults;
  }, [selectedProject, projectStores, storeSearchResults]);

  // Filter stores by search. When a project is selected we narrow the
  // project's stores client-side (cheap). When no project is selected the
  // results already came from the server pre-filtered.
  const filteredStores = useMemo(() => {
    if (!storeSearch.trim() || !selectedProject) return storeSourceList;
    const query = storeSearch.toLowerCase().trim();
    return storeSourceList.filter((s) => {
      const legacyStr = String(s.legacystoreid || '');
      const name = String(s.name || '').toLowerCase();
      const abbrev = String(s.retailer_abbreviation || '').toLowerCase();
      const storeNum = String(s.storenumber || '').toLowerCase();
      return legacyStr.includes(query) || name.includes(query) || abbrev.includes(query) || storeNum.includes(query);
    });
  }, [storeSourceList, storeSearch, selectedProject]);

  const handleSelectRoute = (route: Route) => {
    // Display number: use legacy_store_id - it's 99% populated vs 6% for store_number
    const storeNum = route.store.legacy_store_id || route.store.store_number;
    // Use legacystoreid for URL navigation - StoreDetail resolves to PK via API using projectId
    const storeIdForNav = Number(route.store.legacy_store_id) || route.store.store_id;
    setSelectedRoute({
      routeId: route.route_id,
      storeId: storeIdForNav,
      storeName: `${route.store.retailer_abbreviation} #${storeNum}`,
      storeAddress: `${route.store.address}, ${route.store.city}, ${route.store.state}`,
      projectId: route.project_id,
      projectName: route.project_description,
      distributorId: null, // Routes don't have distributor info
      retailerAbbrev: route.store.retailer_abbreviation,
      storeNumber: storeNum,
    });
    setStep('date');
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  const handleDateContinue = () => {
    // Validate date
    const date = new Date(selectedDate + 'T00:00:00');
    if (!isDateEditable(date)) {
      setError(`Date must be within the editable period (${editablePeriod})`);
      return;
    }
    setError(null);
    setStep('details');
  };

  const handleDetailsContinue = () => {
    // Validate time in/out
    if (!timeIn || !timeOut) {
      setError('Please enter both time in and time out');
      return;
    }

    // Parse times and validate
    const [inHours, inMinutes] = timeIn.split(':').map(Number);
    const [outHours, outMinutes] = timeOut.split(':').map(Number);
    const inTotalMinutes = inHours * 60 + inMinutes;
    const outTotalMinutes = outHours * 60 + outMinutes;

    // Check if overnight (out time is before in time)
    const isOvernight = outTotalMinutes <= inTotalMinutes;

    // Calculate duration
    let durationMinutes = outTotalMinutes - inTotalMinutes;
    if (isOvernight) {
      durationMinutes += 24 * 60; // Add 24 hours for overnight
    }

    // Validate duration is reasonable (e.g., not more than 16 hours)
    if (durationMinutes > 16 * 60) {
      setError('Visit duration cannot exceed 16 hours');
      return;
    }

    // Validate odometer if both provided
    if (driveStartOdo || driveEndOdo) {
      if (!driveStartOdo || !driveEndOdo) {
        setError('Please enter both start and end odometer readings');
        return;
      }
      const startOdo = parseInt(driveStartOdo, 10);
      const endOdo = parseInt(driveEndOdo, 10);
      if (isNaN(startOdo) || isNaN(endOdo)) {
        setError('Odometer readings must be valid numbers');
        return;
      }
      if (endOdo < startOdo) {
        setError('End odometer must be greater than or equal to start odometer');
        return;
      }
    }

    setError(null);
    setStep('confirm');
  };

  const handleStartCall = () => {
    if (!selectedRoute) return;

    // Navigate to StoreDetail with manual entry params including all visit details
    const params = new URLSearchParams({
      projectId: selectedRoute.projectId.toString(),
      manualDate: selectedDate,
      skipGpsCheck: 'true',
      // Pass visit details for retroactive entry
      manualEntry: 'true',
      timeIn,
      timeOut,
    });

    // Add drive time if provided
    if (driveTimeStart && driveTimeEnd) {
      params.append('driveTimeStart', driveTimeStart);
      params.append('driveTimeEnd', driveTimeEnd);
    }

    // Add mileage if provided
    if (driveStartOdo && driveEndOdo) {
      params.append('driveStartOdo', driveStartOdo);
      params.append('driveEndOdo', driveEndOdo);
    }

    // Add team info
    if (isTeamProject) {
      params.append('teamProject', 'true');
      if (isTeamLeader) {
        params.append('teamLeader', 'true');
      }
    }

    // Add notes if provided
    if (visitNotes.trim()) {
      params.append('notes', visitNotes.trim());
    }

    if (selectedRoute.distributorId) {
      params.append('distributorId', selectedRoute.distributorId.toString());
    }

    // Add project description for checkout summary display
    if (selectedRoute.projectName) {
      params.append('projectDesc', selectedRoute.projectName);
    }

    // The reason travels as its human label — StoreDetail stores it straight
    // into the CallLog's off_schedule_reason column, the audit fact's one home.
    if (selectedRoute.isUnassigned) {
      params.append('unassigned', 'true');
      const reasonLabel =
        selectedRoute.unassignedReason === 'other'
          ? (selectedRoute.unassignedReasonOther || '').trim()
          : UNASSIGNED_REASONS.find((r) => r.value === selectedRoute.unassignedReason)?.label || '';
      if (reasonLabel) {
        params.append('unassignedReason', reasonLabel);
      }
    }

    navigate(`/store/${selectedRoute.storeId}?${params.toString()}`);
    onClose();
  };

  // Handle saving edits
  const handleSaveEdit = async () => {
    if (!editingCallLog) return;

    // Validate required fields
    if (!timeIn || !timeOut) {
      setError('Please enter both time in and time out');
      return;
    }

    // Validate odometer if partially filled
    if ((driveStartOdo && !driveEndOdo) || (!driveStartOdo && driveEndOdo)) {
      setError('Please enter both start and end odometer readings');
      return;
    }

    // Validate drive time if partially filled
    if ((driveTimeStart && !driveTimeEnd) || (!driveTimeStart && driveTimeEnd)) {
      setError('Please enter both drive start and end times');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Field-scoped edit (#2061): send ONLY the fields the rep actually
      // changed, diffing against the server values loaded into the form. The
      // backend PATCH is partial, so omitting an untouched field preserves it —
      // this stops a rep who opened the visit before an admin corrected it
      // server-side from clobbering the admin's change with stale form values.
      // API expects full ISO datetime strings for time fields, not HH:MM.
      const buildDateTime = (time: string) => `${selectedDate}T${time}:00`;
      const updateData = buildCallLogEditPatch(
        {
          stcalltime: parseTime(editingCallLog.stcalltime),
          edcalltime: parseTime(editingCallLog.edcalltime),
          teamproject: editingCallLog.teamproject || false,
          teamleader: editingCallLog.teamleader || false,
          notes: editingCallLog.notes || '',
          stdrivetime: parseTime(editingCallLog.stdrivetime),
          eddrivetime: parseTime(editingCallLog.eddrivetime),
          driveodostart: editingCallLog.driveodostart?.toString() || '',
          driveodoend: editingCallLog.driveodoend?.toString() || '',
        },
        { timeIn, timeOut, isTeamProject, isTeamLeader, visitNotes, driveTimeStart, driveTimeEnd, driveStartOdo, driveEndOdo },
        buildDateTime
      );

      if (Object.keys(updateData).length === 0) {
        // Nothing changed — skip the no-op PATCH entirely.
        onClose();
        if (onSaveComplete) onSaveComplete();
        return;
      }

      // #3525 — the manager signed for these two times. Changing them is
      // allowed and recorded; the API refuses the change without a reason, so
      // asking here is the difference between a clear prompt and a 400 the rep
      // cannot act on. Everything else on a signed visit stays freely editable,
      // because the summary they signed never showed it to them.
      const touchesSealed = 'stcalltime' in updateData || 'edcalltime' in updateData;
      if (isEditMode && editingCallLog && isVisitSigned(editingCallLog) && touchesSealed) {
        if (!editReason) {
          setError('This visit was signed by a manager. Choose a reason for changing its times.');
          setIsSaving(false);
          return;
        }
        updateData.edited_after_signature = true;
        updateData.edit_timestamp = new Date().toISOString();
        updateData.edit_reason = editReason;
      }

      // Queue-only (#1987, restored by #3529). This was the one write in the
      // app that still went straight to the network, so an edit made with no
      // signal was simply lost instead of syncing later. The visit is a
      // server-sourced CallLog with no local row, so it is adopted first and
      // the op keys on that uuid. Only the fields buildCallLogEditPatch found
      // CHANGED are carried, so a save cannot clobber a field the rep never
      // touched (#2061).
      const { writeOps, adoptServerCallLog } = await import('../sync/writeOps');
      const visitLocalId = await adoptServerCallLog(editingCallLog.calllogid, {
        storeId: editingCallLog.store_legacystoreid,
        projectId: editingCallLog.project_id ?? undefined,
      });
      await writeOps.editVisit.apply({ visitLocalId, ...updateData } as EditVisitInput);

      // Carry the correction back onto the leg it belongs to (#3519 D6). The
      // PATCH changes the server record and the op's applyLocal touches the
      // visit-session row, but neither reaches the day chain — so without this
      // the rep's own totals, and the prefill the next manual entry reads, keep
      // the value they just corrected.
      const touchesVisitTimes = 'stcalltime' in updateData || 'edcalltime' in updateData;
      const touchesDriveTimes = 'stdrivetime' in updateData || 'eddrivetime' in updateData;
      if (touchesVisitTimes || touchesDriveTimes) {
        applyVisitTimeCorrection(editingCallLog.calllogid, {
          startTime: touchesVisitTimes && timeIn ? new Date(buildDateTime(timeIn)) : undefined,
          endTime: touchesVisitTimes && timeOut ? new Date(buildDateTime(timeOut)) : undefined,
          driveStartTime:
            touchesDriveTimes && driveTimeStart ? new Date(buildDateTime(driveTimeStart)) : undefined,
          driveEndTime: touchesDriveTimes && driveTimeEnd ? new Date(buildDateTime(driveTimeEnd)) : undefined,
        });
      }

      // Close modal and trigger refresh
      onClose();
      if (onSaveComplete) {
        onSaveComplete();
      }
    } catch (err) {
      console.error('Failed to save visit:', err);
      // Extract detailed validation errors from ApiError
      if (err && typeof err === 'object' && 'data' in err) {
        const apiError = err as { data?: Record<string, string[]> };
        if (apiError.data && typeof apiError.data === 'object') {
          // Check for time conflict errors (overlap detection)
          const nonFieldErrors = apiError.data.non_field_errors;
          if (nonFieldErrors && Array.isArray(nonFieldErrors)) {
            // Parse conflict info from error messages
            const conflicts: ConflictingEntry[] = [];
            for (const errorMsg of nonFieldErrors) {
              if (errorMsg.includes('overlapping') || errorMsg.includes('conflicts with')) {
                const conflict = parseConflictFromError(errorMsg);
                if (conflict) {
                  conflicts.push(conflict);
                }
              }
            }

            if (conflicts.length > 0) {
              // Show time conflict editor instead of generic error
              setTimeConflicts(conflicts);
              setShowTimeConflictEditor(true);
              // Go back to details step so user can edit times
              setStep('details');
              return;
            }

            // Other non-field errors
            setError(nonFieldErrors.join(', '));
            return;
          }

          // Format other field errors
          const fieldErrors = Object.entries(apiError.data)
            .filter(([field]) => field !== 'non_field_errors')
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
          setError(fieldErrors || 'Failed to save changes');
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle deleting a visit
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!editingCallLog) return;

    setIsDeleting(true);
    setError(null);

    try {
      // Queue-only, like every other write and like the Time page's own delete
      // (#3529). A direct call here meant deleting a visit with no signal
      // failed outright, and it was a second delete path besides.
      const { writeOps, adoptServerCallLog } = await import('../sync/writeOps');
      const visitLocalId = await adoptServerCallLog(editingCallLog.calllogid, {
        storeId: editingCallLog.store_legacystoreid,
        projectId: editingCallLog.project_id ?? undefined,
      });
      await writeOps.deleteTimeEntry.apply({ visitLocalId });
      setShowDeleteConfirm(false);
      onClose();
      if (onSaveComplete) {
        onSaveComplete();
      }
    } catch (err) {
      console.error('Failed to delete visit:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete visit');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle time conflict editor callbacks
  const handleTimeConflictTimesChanged = useCallback((newTimeIn: string, newTimeOut: string) => {
    setTimeIn(newTimeIn);
    setTimeOut(newTimeOut);
  }, []);

  const handleTimeConflictRetry = useCallback(() => {
    // Clear conflict state and retry submission
    setTimeConflicts([]);
    setShowTimeConflictEditor(false);
    setError(null);
    // Go to confirm step to re-submit
    setStep('confirm');
  }, []);

  const handleTimeConflictCancel = useCallback(() => {
    // Clear conflict state but keep user on details step
    setTimeConflicts([]);
    setShowTimeConflictEditor(false);
    setError(null);
  }, []);

  const handleBack = () => {
    // Clear time conflict state when going back
    if (showTimeConflictEditor) {
      setShowTimeConflictEditor(false);
      setTimeConflicts([]);
    }

    // In edit mode, back from details closes the modal
    if (isEditMode && step === 'details') {
      onClose();
      return;
    }
    // In edit mode, back from confirm goes to details
    if (isEditMode && step === 'confirm') {
      setStep('details');
      return;
    }

    if (step === 'date') {
      if (selectedRoute?.isUnassigned) {
        setStep('unassigned');
      } else {
        setStep('select');
      }
    } else if (step === 'details') {
      setStep('date');
    } else if (step === 'confirm') {
      setStep('details');
    } else if (step === 'unassigned') {
      setStep('select');
      setProjectSearch('');
      setStoreSearch('');
      setSelectedProject(null);
      setSelectedStore(null);
      setUnassignedReason('');
      setUnassignedReasonOther('');
    }
  };

  // Handle starting unassigned flow
  const handleStartUnassigned = () => {
    setStep('unassigned');
    setError(null);
  };

  // Handle project selection from combobox
  const handleSelectProject = (project: Project) => {
    setSelectedProject(project);
    setProjectSearch(`${project.projectid} - ${project.projectdesc || project.projectname}`);
    setShowProjectDropdown(false);
    // If the rep had a store picked from the cross-filter side and it
    // belongs to this project, keep it; otherwise clear. We re-run the
    // store-for-project fetch (via useEffect on selectedProject) and
    // the JSX clears the store search if the store doesn't match. #1851
    if (selectedStore && projectsForStore.length > 0) {
      const stillValid = projectsForStore.some((p) => p.projectid === project.projectid);
      if (!stillValid) {
        setSelectedStore(null);
        setStoreSearch('');
      }
    } else if (selectedStore) {
      // Store was picked without a cross-filter context - clear it to
      // be safe; the new project may not contain it.
      setSelectedStore(null);
      setStoreSearch('');
    }
    // Stop using the typeahead results once a project narrows the list.
    setStoreSearchResults([]);
  };

  // Handle store selection. Works for both the in-project list AND the
  // server-side typeahead results. If the store wasn't chosen from
  // within a selected project, fetch its projects and narrow the
  // project picker. #1851
  const handleSelectStore = useCallback(
    async (store: ProjectStore) => {
      setSelectedStore(store);
      const displayName = store.retailer_abbreviation
        ? `${store.legacystoreid} - ${store.retailer_abbreviation} #${store.legacystoreid}`
        : `${store.legacystoreid} - ${store.name || 'Store'}`;
      setStoreSearch(displayName);
      setShowStoreDropdown(false);

      // If no project is selected yet, this is a store-first pick - fetch
      // the projects-for-store cross-filter so the project picker narrows.
      if (!selectedProject) {
        setIsLoadingProjectsForStore(true);
        try {
          const projects = await storesApi.getProjectsByStore(store.id);
          setProjectsForStore(projects);
        } catch (err) {
          console.error('Failed to fetch projects for store:', err);
          setProjectsForStore([]);
        } finally {
          setIsLoadingProjectsForStore(false);
        }
      }
    },
    [selectedProject]
  );


  // Handle proceeding with unassigned call
  const handleProceedWithUnassigned = () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }
    if (!selectedStore) {
      setError('Please select a store');
      return;
    }
    if (!unassignedReason) {
      setError('Please select a reason for the unassigned call');
      return;
    }
    if (unassignedReason === 'other' && !unassignedReasonOther.trim()) {
      setError('Please provide a reason');
      return;
    }

    // Display: use legacystoreid for human-friendly store number
    const storeName = selectedStore.retailer_abbreviation
      ? `${selectedStore.retailer_abbreviation} #${selectedStore.legacystoreid}`
      : selectedStore.name || `Store ${selectedStore.legacystoreid}`;

    setSelectedRoute({
      routeId: null, // No route for unassigned
      // Use legacystoreid for URL navigation - StoreDetail resolves to PK via API using projectId
      storeId: selectedStore.legacystoreid,
      storeName,
      storeAddress: selectedStore.address,
      projectId: selectedProject.projectid,
      projectName: selectedProject.projectdesc || selectedProject.projectname || `Project ${selectedProject.projectid}`,
      distributorId: null,
      retailerAbbrev: selectedStore.retailer_abbreviation || '',
      storeNumber: String(selectedStore.legacystoreid),  // Display number (legacystoreid)
      isUnassigned: true,
      unassignedReason,
      unassignedReasonOther: unassignedReason === 'other' ? unassignedReasonOther : undefined,
    });
    setError(null);
    setStep('date');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      {/* Allow overflow for unassigned step so dropdowns are visible */}
      <div className={`flex w-full max-w-md flex-col rounded-xl bg-white shadow-xl ${step === 'unassigned' ? 'max-h-[95vh] overflow-visible' : 'max-h-[90vh]'}`}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <div className="flex items-center gap-3">
            {step !== 'select' && (
              <button
                onClick={handleBack}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="font-heading text-lg font-semibold text-slate-800">
              {isEditMode ? (
                isSigned ? (
                  'View Visit'
                ) : (
                  <>
                    {step === 'details' && 'Edit Visit'}
                    {step === 'confirm' && 'Confirm Changes'}
                  </>
                )
              ) : (
                <>
                  {step === 'select' && 'Add time'}
                  {step === 'unassigned' && 'Unassigned Call/Set'}
                  {step === 'date' && 'Select Date'}
                  {step === 'details' && 'Visit Details'}
                  {step === 'confirm' && 'Confirm Entry'}
                </>
              )}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-1.5 border-b border-slate-100 px-4 py-3">
          {isEditMode && isSigned ? (
            /* Signed visit: show locked indicator instead of steps */
            <div className="flex items-center gap-2 text-sm text-green-700">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="font-medium">Signed & Verified</span>
            </div>
          ) : isEditMode ? (
            <>
              {/* Edit mode (unsigned): 2 steps - Edit -> Save */}
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                step === 'details' ? 'bg-primary-600 text-white' : 'bg-primary-100 text-primary-600'
              }`}>
                1
              </div>
              <div className={`h-0.5 w-8 ${step === 'confirm' ? 'bg-primary-600' : 'bg-slate-200'}`} />
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                step === 'confirm' ? 'bg-primary-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                2
              </div>
            </>
          ) : (
            <>
              {/* Add mode: 4 steps - Store, Date, Details, Confirm */}
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                step === 'select' || step === 'unassigned' ? 'bg-primary-600 text-white' : 'bg-primary-100 text-primary-600'
              }`}>
                1
              </div>
              <div className={`h-0.5 w-6 ${step !== 'select' && step !== 'unassigned' ? 'bg-primary-600' : 'bg-slate-200'}`} />
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                step === 'date' ? 'bg-primary-600 text-white' :
                (step === 'details' || step === 'confirm') ? 'bg-primary-100 text-primary-600' : 'bg-slate-200 text-slate-500'
              }`}>
                2
              </div>
              <div className={`h-0.5 w-6 ${step === 'details' || step === 'confirm' ? 'bg-primary-600' : 'bg-slate-200'}`} />
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                step === 'details' ? 'bg-primary-600 text-white' :
                step === 'confirm' ? 'bg-primary-100 text-primary-600' : 'bg-slate-200 text-slate-500'
              }`}>
                3
              </div>
              <div className={`h-0.5 w-6 ${step === 'confirm' ? 'bg-primary-600' : 'bg-slate-200'}`} />
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                step === 'confirm' ? 'bg-primary-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                4
              </div>
            </>
          )}
        </div>

        {/* Content */}
        {/* Use overflow-visible for unassigned step to allow dropdowns to extend beyond container */}
        <div className={`flex-1 p-4 ${step === 'unassigned' ? 'overflow-visible' : 'overflow-y-auto'}`}>
          {/* Time Conflict Editor - shown when overlap errors occur */}
          {showTimeConflictEditor && timeConflicts.length > 0 && step === 'details' && (
            <div className="mb-4">
              <TimeConflictEditor
                errorMessage={timeConflicts.map(c => c.conflictType).join('; ')}
                conflictingEntries={timeConflicts}
                proposedTimeIn={timeIn}
                proposedTimeOut={timeOut}
                proposedDate={selectedDate}
                onTimesChanged={handleTimeConflictTimesChanged}
                onRetry={handleTimeConflictRetry}
                onCancel={handleTimeConflictCancel}
              />
            </div>
          )}

          {/* Error Message - only show if NOT showing conflict editor */}
          {error && !showTimeConflictEditor && (
            <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Step 1: Select Project/Store */}
          {step === 'select' && (
            <>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
                </div>
              ) : (
                <>
                  {/* No routes message */}
                  {routesData?.routes.length === 0 && (
                    <div className="mb-4 py-4 text-center">
                      <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                        <svg className="h-6 w-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                      </div>
                      <p className="font-medium text-slate-700">No Routes Assigned</p>
                      <p className="mt-1 text-sm text-slate-500">
                        You don't have any routes assigned for the current billing period.
                      </p>
                      <p className="mt-2 text-sm text-slate-500">
                        Use "Add Unassigned Call/Set" below to enter a call.
                      </p>
                    </div>
                  )}

                  {/* Search Input - only show if routes exist */}
                  {routesData && routesData.routes.length > 0 && (
                    <div className="mb-4">
                      <div className="relative">
                        <svg
                          className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          placeholder="Search stores, projects..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-4 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                        />
                      </div>
                    </div>
                  )}

                  {/* Routes List */}
                  <div className="space-y-4">
                    {groupedRoutes.map(([projectName, routes]) => (
                      <div key={projectName}>
                        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                          {projectName}
                        </h3>
                        <div className="space-y-2">
                          {routes.map((route) => (
                            <button
                              key={route.route_id}
                              onClick={() => handleSelectRoute(route)}
                              className="flex w-full items-center gap-3 rounded-lg border border-slate-200 p-3 text-left transition-colors hover:bg-slate-50"
                            >
                              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-800 truncate">
                                    {route.store.retailer_abbreviation} #{route.store.legacy_store_id || route.store.store_number}
                                  </span>
                                  <span className="flex-shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                                    P{route.project_id}
                                  </span>
                                </div>
                                <div className="text-sm text-slate-500 truncate">
                                  {route.store.address}, {route.store.city}
                                </div>
                                {route.visits_remaining > 0 && (
                                  <div className="mt-1 text-xs text-amber-600">
                                    {route.visits_remaining} visit{route.visits_remaining !== 1 ? 's' : ''} remaining
                                  </div>
                                )}
                              </div>
                              <svg className="h-5 w-5 flex-shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                    {filteredRoutes.length === 0 && searchQuery && (
                      <div className="py-8 text-center">
                        <p className="text-slate-500">No stores match "{searchQuery}"</p>
                      </div>
                    )}
                  </div>

                  {/* Unassigned Call Option */}
                  <div className="mt-6 border-t border-slate-200 pt-4">
                    <button
                      onClick={handleStartUnassigned}
                      className="flex w-full items-center gap-3 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 p-4 text-left transition-colors hover:bg-amber-100"
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-amber-800">Add Unassigned Call/Set</div>
                        <div className="text-sm text-amber-600">
                          Enter Project # and Store # manually
                        </div>
                      </div>
                      <svg className="h-5 w-5 flex-shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* Step: Unassigned Selection - Searchable Comboboxes */}
          {step === 'unassigned' && (
            <div className="space-y-4">
              {/* Info Banner */}
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 flex-shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-amber-800">Unassigned Call</p>
                    <p className="text-xs text-amber-700">
                      Type to search either field. Picking one narrows the other.
                    </p>
                  </div>
                </div>
              </div>

              {/* Project Combobox */}
              <div ref={projectDropdownRef} className="relative">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Project <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => {
                      setProjectSearch(e.target.value);
                      setSelectedProject(null);
                      setShowProjectDropdown(true);
                    }}
                    onFocus={() => setShowProjectDropdown(true)}
                    placeholder={
                      isLoadingProjects
                        ? 'Loading projects...'
                        : isLoadingProjectsForStore
                          ? 'Loading projects for selected store...'
                          : selectedStore
                            ? `Pick from ${projectsForStore.length} project${projectsForStore.length === 1 ? '' : 's'} at this store`
                            : 'Type project # or name...'
                    }
                    disabled={isLoadingProjects || isLoadingProjectsForStore}
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 pr-10 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:bg-slate-100"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    {(isLoadingProjects || isLoadingProjectsForStore) ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
                    ) : (
                      <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </div>
                {/* Project Dropdown */}
                {showProjectDropdown && filteredProjects.length > 0 && (
                  <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                    {filteredProjects.slice(0, 50).map((project) => (
                      <button
                        key={project.projectid}
                        onClick={() => handleSelectProject(project)}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-slate-50"
                      >
                        <span className="font-mono text-sm font-medium text-primary-600">{project.projectid}</span>
                        <span className="text-slate-600">-</span>
                        <span className="truncate text-sm text-slate-800">{project.projectdesc || project.projectname}</span>
                      </button>
                    ))}
                    {filteredProjects.length > 50 && (
                      <div className="px-4 py-2 text-center text-xs text-slate-500">
                        Type more to narrow results ({filteredProjects.length} total)
                      </div>
                    )}
                  </div>
                )}
                {showProjectDropdown && !isLoadingProjects && filteredProjects.length === 0 && projectSearch && (
                  <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white p-3 text-center text-sm text-slate-500 shadow-lg">
                    No projects match "{projectSearch}"
                  </div>
                )}
              </div>

              {/* Store Combobox - always visible. Source depends on whether a
                  project is selected: in-project list (existing) OR server-side
                  typeahead via /api/retailers/stores/search/ (#1851). */}
              <div ref={storeDropdownRef} className="relative">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Store <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={storeSearch}
                    onChange={(e) => {
                      setStoreSearch(e.target.value);
                      // Clearing the store also clears the inverse project
                      // filter so the project dropdown returns to its full
                      // list when no store is locked in. #1851
                      if (selectedStore) {
                        setSelectedStore(null);
                        setProjectsForStore([]);
                      }
                      setShowStoreDropdown(true);
                    }}
                    onFocus={() => setShowStoreDropdown(true)}
                    placeholder={
                      isLoadingStores
                        ? 'Loading project stores...'
                        : selectedProject
                          ? 'Type store # or name within this project...'
                          : 'Type a store # to search across all retailers...'
                    }
                    disabled={isLoadingStores}
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 pr-10 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:bg-slate-100"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    {(isLoadingStores || isSearchingStores) ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
                    ) : (
                      <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </div>
                {/* Store Dropdown */}
                {showStoreDropdown && filteredStores.length > 0 && (
                  <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                    {filteredStores.slice(0, 50).map((store) => (
                      <button
                        key={store.id}
                        onClick={() => handleSelectStore(store)}
                        className="flex w-full flex-col px-4 py-2 text-left hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-primary-600">{store.legacystoreid}</span>
                          <span className="text-slate-600">-</span>
                          <span className="truncate text-sm font-medium text-slate-800">
                            {store.retailer_abbreviation
                              ? `${store.retailer_abbreviation} #${store.legacystoreid}`
                              : store.name || `Store ${store.legacystoreid}`}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 truncate">{store.address}</div>
                      </button>
                    ))}
                    {filteredStores.length > 50 && (
                      <div className="px-4 py-2 text-center text-xs text-slate-500">
                        Type more to narrow results ({filteredStores.length} total)
                      </div>
                    )}
                  </div>
                )}
                {showStoreDropdown && !isLoadingStores && !isSearchingStores && filteredStores.length === 0 && storeSearch && (
                  <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white p-3 text-center text-sm text-slate-500 shadow-lg">
                    No stores match "{storeSearch}"
                  </div>
                )}
                {selectedProject && !isLoadingStores && projectStores.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">
                    No stores found for this project. Try a different project.
                  </p>
                )}
              </div>

              {/* Selected Store Summary */}
              {selectedStore && (
                <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                  <div className="flex items-start gap-2">
                    <svg className="h-5 w-5 flex-shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        {selectedStore.retailer_abbreviation
                          ? `${selectedStore.retailer_abbreviation} #${selectedStore.legacystoreid}`
                          : selectedStore.name || `Store ${selectedStore.legacystoreid}`}
                      </p>
                      <p className="text-xs text-green-700">{selectedStore.address}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Reason Selection (Required) - Only show after store is selected */}
              {selectedStore && (
                <>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Reason for unassigned call <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={unassignedReason}
                      onChange={(e) => setUnassignedReason(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    >
                      <option value="">Select a reason...</option>
                      {UNASSIGNED_REASONS.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Other reason text input */}
                  {unassignedReason === 'other' && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Please specify <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={unassignedReasonOther}
                        onChange={(e) => setUnassignedReasonOther(e.target.value)}
                        placeholder="Enter reason..."
                        className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      />
                    </div>
                  )}

                  {/* Continue Button */}
                  <button
                    onClick={handleProceedWithUnassigned}
                    disabled={!unassignedReason || (unassignedReason === 'other' && !unassignedReasonOther.trim())}
                    className="w-full rounded-lg bg-primary-600 py-3 font-semibold text-white transition-colors hover:bg-primary-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                  >
                    Continue
                  </button>
                </>
              )}
            </div>
          )}

          {/* Step 2: Date Selection */}
          {step === 'date' && (
            <div className="space-y-4">
              {/* Selected Store Summary */}
              {selectedRoute && (
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-sm text-slate-500">Selected Store</div>
                  <div className="font-medium text-slate-800">{selectedRoute.storeName}</div>
                  <div className="text-sm text-slate-600">{selectedRoute.projectName}</div>
                </div>
              )}

              {/* Editable Period Notice */}
              <div className="rounded-lg bg-blue-50 p-3">
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-blue-800">
                    Valid dates: <span className="font-medium">{editablePeriod}</span>
                  </span>
                </div>
              </div>

              {/* Date Picker */}
              <div>
                <label htmlFor="callDate" className="mb-2 block text-sm font-medium text-slate-700">
                  Call Date
                </label>
                <input
                  id="callDate"
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  min={formatDateToLocal(minDate)}
                  max={formatDateToLocal(maxDate)}
                  className="w-full max-w-full rounded-lg border border-slate-300 px-3 py-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                />
              </div>

              {/* Continue Button */}
              <button
                onClick={handleDateContinue}
                disabled={!selectedDate}
                className="w-full rounded-lg bg-primary-600 py-3 font-semibold text-white transition-colors hover:bg-primary-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 3: Visit Details */}
          {step === 'details' && (
            <div className="space-y-4">
              {/* Signed Visit Warning - Edit Mode Only */}
              {isEditMode && isSigned && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                  <div className="flex items-start gap-2">
                    <svg className="h-5 w-5 flex-shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-amber-800">Signed Visit</p>
                      <p className="text-xs text-amber-700">
                        This visit has been signed by a manager. Changes will be tracked and require a reason.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Selected Store Summary */}
              {selectedRoute && (
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-800">{selectedRoute.storeName}</div>
                      <div className="text-sm text-slate-600">{selectedRoute.projectName}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-slate-700">
                        {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 1. Drive Time */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  <span className="inline-flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">1</span>
                    Drive Time
                  </span>
                  <span className="ml-2 text-xs font-normal text-slate-400">(optional)</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="min-w-0">
                    <input
                      type="time"
                      value={driveTimeStart}
                      onChange={(e) => setDriveTimeStart(e.target.value)}
                      className="w-full max-w-full rounded-lg border border-slate-300 px-2 py-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                    <span className="mt-1 block text-xs text-slate-500">Left for store</span>
                  </div>
                  <div className="min-w-0">
                    <input
                      type="time"
                      value={driveTimeEnd}
                      onChange={(e) => {
                        const arrivedTime = e.target.value;
                        setDriveTimeEnd(arrivedTime);
                        // Auto-fill "Time In" from "Arrived at store" (only in ADD mode, only if empty)
                        if (!editingCallLog && arrivedTime && !timeIn) {
                          setTimeIn(arrivedTime);
                        }
                      }}
                      className="w-full max-w-full rounded-lg border border-slate-300 px-2 py-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                    <span className="mt-1 block text-xs text-slate-500">Arrived at store</span>
                  </div>
                </div>
                {/* Drive duration preview */}
                {driveTimeStart && driveTimeEnd && (() => {
                  const [startH, startM] = driveTimeStart.split(':').map(Number);
                  const [endH, endM] = driveTimeEnd.split(':').map(Number);
                  let mins = (endH * 60 + endM) - (startH * 60 + startM);
                  if (mins < 0) mins += 24 * 60;
                  const hours = Math.floor(mins / 60);
                  const minutes = mins % 60;
                  return (
                    <div className="mt-2 rounded-lg bg-blue-50 p-2 text-center text-sm">
                      <span className="text-blue-600">Drive time: </span>
                      <span className="font-medium text-blue-800">{hours}h {minutes}m</span>
                    </div>
                  );
                })()}
              </div>

              {/* 2. Odometer */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  <span className="inline-flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">2</span>
                    Odometer
                  </span>
                  <span className="ml-2 text-xs font-normal text-slate-400">(optional)</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="min-w-0">
                    <input
                      type="number"
                      inputMode="numeric"
                      value={driveStartOdo}
                      onChange={(e) => setDriveStartOdo(e.target.value)}
                      placeholder="Start"
                      className="w-full max-w-full rounded-lg border border-slate-300 px-2 py-3 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                    <span className="mt-1 block text-xs text-slate-500">Start Odometer</span>
                  </div>
                  <div className="min-w-0">
                    <input
                      type="number"
                      inputMode="numeric"
                      value={driveEndOdo}
                      onChange={(e) => setDriveEndOdo(e.target.value)}
                      placeholder="End"
                      className="w-full max-w-full rounded-lg border border-slate-300 px-2 py-3 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                    <span className="mt-1 block text-xs text-slate-500">End Odometer</span>
                  </div>
                </div>
                {/* Miles preview */}
                {driveStartOdo && driveEndOdo && parseInt(driveEndOdo, 10) >= parseInt(driveStartOdo, 10) && (
                  <div className="mt-2 rounded-lg bg-blue-50 p-2 text-center">
                    <span className="text-sm text-blue-600">
                      Miles: <span className="font-semibold">{parseInt(driveEndOdo, 10) - parseInt(driveStartOdo, 10)}</span>
                    </span>
                  </div>
                )}
              </div>

              {/* 3. Store Time */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  <span className="inline-flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-xs font-bold text-green-700">3</span>
                    Store Time
                  </span>
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="min-w-0">
                    <input
                      type="time"
                      value={timeIn}
                      onChange={(e) => setTimeIn(e.target.value)}
                      className="w-full max-w-full rounded-lg border border-slate-300 px-2 py-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                    <span className="mt-1 block text-xs text-slate-500">Time In</span>
                  </div>
                  <div className="min-w-0">
                    <input
                      type="time"
                      value={timeOut}
                      onChange={(e) => setTimeOut(e.target.value)}
                      className="w-full max-w-full rounded-lg border border-slate-300 px-2 py-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                    <span className="mt-1 block text-xs text-slate-500">Time Out</span>
                  </div>
                </div>
                {/* A manager signed for these two times (#3525). Changing them
                    is allowed and recorded — the API refuses the change without
                    a reason, so it is asked for here rather than returned as a
                    400 the rep cannot act on. */}
                {isEditMode && editingCallLog && isVisitSigned(editingCallLog) && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="signed-edit-reason">
                    <p className="text-xs text-amber-800">
                      A manager signed for this visit&rsquo;s times. You can still correct them —
                      the change is recorded with your reason.
                    </p>
                    <label className="mt-2 block text-xs font-medium text-amber-900" htmlFor="signed-edit-reason-select">
                      Reason for the change
                    </label>
                    <select
                      id="signed-edit-reason-select"
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900"
                    >
                      <option value="">Choose a reason&hellip;</option>
                      {EDIT_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Store duration preview */}
                {timeIn && timeOut && (() => {
                  const [inH, inM] = timeIn.split(':').map(Number);
                  const [outH, outM] = timeOut.split(':').map(Number);
                  let mins = (outH * 60 + outM) - (inH * 60 + inM);
                  const isOvernight = mins <= 0;
                  if (isOvernight) mins += 24 * 60;
                  const hours = Math.floor(mins / 60);
                  const minutes = mins % 60;
                  return (
                    <div className="mt-2 flex items-center gap-2 rounded-lg bg-green-50 p-2 text-center text-sm">
                      <span className="text-green-600">Store time:</span>
                      <span className="font-medium text-green-800">{hours}h {minutes}m</span>
                      {isOvernight && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">Overnight</span>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Team Project Toggle */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Team Project?
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsTeamProject(false);
                      setIsTeamLeader(false);
                    }}
                    className={`flex-1 rounded-lg py-2.5 font-medium transition-colors ${
                      !isTeamProject
                        ? 'bg-slate-700 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsTeamProject(true)}
                    className={`flex-1 rounded-lg py-2.5 font-medium transition-colors ${
                      isTeamProject
                        ? 'bg-primary-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    Yes
                  </button>
                </div>
              </div>

              {/* Team Leader Toggle - only show if team project */}
              {isTeamProject && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Are you the team leader?
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setIsTeamLeader(false)}
                      className={`flex-1 rounded-lg py-2.5 font-medium transition-colors ${
                        !isTeamLeader
                          ? 'bg-slate-700 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsTeamLeader(true)}
                      className={`flex-1 rounded-lg py-2.5 font-medium transition-colors ${
                        isTeamLeader
                          ? 'bg-primary-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              )}

              {/* Visit Notes */}
              <div>
                <label htmlFor="visitNotes" className="mb-2 block text-sm font-medium text-slate-700">
                  Visit Notes
                  <span className="ml-2 text-xs font-normal text-slate-400">(optional)</span>
                </label>
                <textarea
                  id="visitNotes"
                  value={visitNotes}
                  onChange={(e) => setVisitNotes(e.target.value)}
                  placeholder="Any notes about this visit..."
                  rows={2}
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                />
              </div>


              {/* Continue Button */}
              <button
                onClick={handleDetailsContinue}
                disabled={!timeIn || !timeOut}
                className="w-full rounded-lg bg-primary-600 py-3 font-semibold text-white transition-colors hover:bg-primary-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 'confirm' && selectedRoute && (
            <div className="space-y-4">
              {/* Signed Visit: Locked Notice */}
              {isEditMode && isSigned && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                      <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-green-800">Signed Visit</p>
                      <p className="text-sm text-green-700">
                        A manager signed for this visit&rsquo;s times. Changing them is recorded
                        with your reason; everything else stays editable as usual.
                      </p>
                      {editingCallLog?.manager_name && (
                        <p className="mt-1 text-xs text-green-600">
                          Signed by: {editingCallLog.manager_name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Summary Card */}
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                  Visit Summary
                </h3>

                <div className="space-y-3">
                  {/* Store */}
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-slate-500">Store</div>
                      <div className="font-medium text-slate-800">{selectedRoute.storeName}</div>
                      <div className="text-sm text-slate-500">{selectedRoute.storeAddress}</div>
                    </div>
                  </div>

                  {/* Project */}
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Project</div>
                      <div className="font-medium text-slate-800">{selectedRoute.projectName}</div>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Date</div>
                      <div className="font-medium text-slate-800">
                        {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Drive Time - only if provided */}
                  {driveTimeStart && driveTimeEnd && (
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Drive Time</div>
                        <div className="font-medium text-slate-800">
                          {driveTimeStart} - {driveTimeEnd}
                          {(() => {
                            const [startH, startM] = driveTimeStart.split(':').map(Number);
                            const [endH, endM] = driveTimeEnd.split(':').map(Number);
                            let mins = (endH * 60 + endM) - (startH * 60 + startM);
                            if (mins < 0) mins += 24 * 60;
                            const hours = Math.floor(mins / 60);
                            const minutes = mins % 60;
                            return ` (${hours}h ${minutes}m)`;
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mileage - only if provided */}
                  {driveStartOdo && driveEndOdo && (
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-600">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Odometer</div>
                        <div className="font-medium text-slate-800">
                          {parseInt(driveEndOdo, 10) - parseInt(driveStartOdo, 10)} miles
                        </div>
                        <div className="text-xs text-slate-500">
                          {driveStartOdo} &rarr; {driveEndOdo}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Store Time */}
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-slate-500">Store Time</div>
                      <div className="font-medium text-slate-800">
                        {timeIn} - {timeOut}
                        {(() => {
                          const [inH, inM] = timeIn.split(':').map(Number);
                          const [outH, outM] = timeOut.split(':').map(Number);
                          let mins = (outH * 60 + outM) - (inH * 60 + inM);
                          if (mins <= 0) mins += 24 * 60;
                          const hours = Math.floor(mins / 60);
                          const minutes = mins % 60;
                          return ` (${hours}h ${minutes}m)`;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Team info - only if team project */}
                  {isTeamProject && (
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Team</div>
                        <div className="font-medium text-slate-800">
                          Team Project {isTeamLeader && '(Leader)'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes - only if provided */}
                  {visitNotes.trim() && (
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Notes</div>
                        <div className="text-sm text-slate-700">{visitNotes}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Unassigned Call Warning */}
              {selectedRoute.isUnassigned && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <div className="flex items-start gap-2">
                    <svg className="h-5 w-5 flex-shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-amber-800">Unassigned Call/Set</p>
                      <p className="text-xs text-amber-700">
                        Reason: {UNASSIGNED_REASONS.find(r => r.value === selectedRoute.unassignedReason)?.label || selectedRoute.unassignedReason}
                        {selectedRoute.unassignedReasonOther && ` - ${selectedRoute.unassignedReasonOther}`}
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        This entry will be flagged for manager review.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Mode: Manual Entry Notice */}
              {!isEditMode && (
                <div className="rounded-lg bg-blue-50 p-3">
                  <div className="flex items-start gap-2">
                    <svg className="h-5 w-5 flex-shrink-0 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">Manual Entry</p>
                      <p className="text-xs text-blue-700">
                        Next, you'll complete the survey and any required photos/signature.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Mode: Continue to Survey Button */}
              {!isEditMode && (
                <button
                  onClick={handleStartCall}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 py-4 font-semibold text-white transition-colors hover:bg-green-700"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Continue to Survey
                </button>
              )}

              {/* Edit Mode: Save Changes. Signed visits save too — the seal is
                  the server's, scoped to the visit times, and handleSaveEdit
                  collects the reason when those change. A save-less signed
                  editor made every correction the seal allows dead-end here. */}
              {isEditMode && (
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 py-4 font-semibold text-white transition-colors hover:bg-green-700 disabled:bg-slate-300"
                >
                  {isSaving ? (
                    <>
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Save Changes
                    </>
                  )}
                </button>
              )}

              {/* Edit Mode (Unsigned): Delete Button */}
              {isEditMode && !isSigned && !showDeleteConfirm && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 bg-white py-3 font-medium text-red-600 transition-colors hover:bg-red-50"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Visit
                </button>
              )}

              {/* Delete Confirmation */}
              {isEditMode && showDeleteConfirm && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="mb-3 flex items-start gap-2">
                    <svg className="h-5 w-5 flex-shrink-0 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-red-800">Delete this visit?</p>
                      <p className="text-xs text-red-700">
                        This action cannot be undone. All data for this visit will be permanently removed.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className="flex-1 rounded-lg border border-slate-200 bg-white py-2.5 font-medium text-slate-600 transition-colors hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 py-2.5 font-medium text-white transition-colors hover:bg-red-700 disabled:bg-slate-300"
                    >
                      {isDeleting ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          Deleting...
                        </>
                      ) : (
                        'Delete'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
